import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, CheckCircle2, ClipboardList, Mail, MessageCircle, RefreshCw, UserPlus, Users, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Card } from "@/components/app/Primitives";
import { StackPage } from "@/components/app/Layout";
import { CenterSpinner } from "@/components/app/Spinner";

const ICONS = {
  task_assigned: ClipboardList, task_status: RefreshCw, task_comment: MessageCircle, approval_pending: CheckCheck,
  task_approved: CheckCircle2, task_rejected: XCircle, invite: Mail, join_request: UserPlus, member_approved: Users,
};

export default function Notifications() {
  const navigate = useNavigate();
  const toast = useToast();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setNotes((await api("/notifications")).items || []); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed to load", "error"); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const open = async (n) => {
    if (!n.read) {
      try { await api(`/notifications/${n.id}/read`, { method: "PATCH" }); } catch { /* noop */ }
      setNotes((p) => p.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.task_id) navigate(`/task/${n.task_id}`);
    else if (n.workspace_id) navigate("/workspaces");
  };
  const markAll = async () => {
    try { await api("/notifications/mark-all-read", { method: "POST" }); setNotes((p) => p.map((x) => ({ ...x, read: true }))); toast.show("All read", "info"); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); }
  };
  const unread = notes.filter((n) => !n.read).length;

  return (
    <StackPage title="Notifications" testID="notifications-page" right={unread > 0 ? <button onClick={markAll} className="text-[13px] font-bold text-a-primary" data-testid="notifications-mark-all">Mark all read</button> : null} contentClassName="p-4 space-y-2.5">
      {loading ? <CenterSpinner /> : notes.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center gap-2 py-5 text-center">
            <Bell size={36} className="text-a-muted" />
            <p className="font-bold text-a-text">You&apos;re all caught up</p>
            <p className="text-a-muted">Task assignments, approvals and comments will appear here.</p>
          </div>
        </Card>
      ) : notes.map((n) => {
        const Icon = ICONS[n.type] || Bell;
        return (
          <Card key={n.id} onClick={() => open(n)} testID={`notification-${n.id}`} className={n.read ? "" : "!border-a-primary"}>
            <div className="flex gap-3">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-a-primary ${n.read ? "bg-a-bg" : "bg-a-dim"}`}><Icon size={20} /></span>
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className={`text-sm text-a-text ${n.read ? "font-semibold" : "font-extrabold"}`}>{n.title}</p>
                <p className="line-clamp-2 text-[13px] text-a-muted">{n.body}</p>
                <p className="mt-0.5 text-[11px] text-a-muted">{new Date(n.created_at).toLocaleString()}</p>
              </div>
              {!n.read ? <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-a-primary" /> : null}
            </div>
          </Card>
        );
      })}
    </StackPage>
  );
}
